import React, { useMemo, useCallback, useContext, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Trans } from 'react-i18next';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getDaysOfWeek } from '@ubeya/shared/utils/time';
import { FlexColumn, Flex, FlexCenter, Loader, card } from '@ubeya/shared-web/components';
import { maxMedia } from '@ubeya/shared-web/style';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useTags from '@ubeya/shared/hooks/account/useTags';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { selectedBranch } from '@ubeya/shared/atoms/shared';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { SORT_BY_TIME } from '@ubeya/shared/utils/sorting';
import useSuppliers from '@ubeya/shared/hooks/account/useSuppliers';
import withPermissions from '@ubeya/shared-web/hoc/withPermissions';
import privileges from '@ubeya/shared-web/privileges';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { getWeekKeyForDate } from '@ubeya/shared/utils/scheduling';
import { isTablet } from 'react-device-detect';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import {
  selectedCopyEvent,
  selectedEventModal,
  newOrder,
  quickProject,
  selectedShiftModal,
  selectedBookingModal,
  selectedPublishProject,
  showAllBranchesMode,
  schedulingHideWeeklyPlanners,
  schedulingShowOnlyMainEvents,
  selectedMainProjectView,
  projectFilters
} from 'shared/atoms/scheduling';
import { filterMainProjectsByDateForMainEventSelection } from 'shared/utils/scheduling';
import { SchedulingContextSelector, SchedulingContextDispatcher } from 'scheduling/contexts/SchedulingContext';
import WeeklyPlannerBar from 'scheduling/components/WeeklyPlannerBar';
import MainProjectBar from 'scheduling/components/MainProjectBar';
import { getMainProjectBarTrackCount } from 'scheduling/mainProjectBarLayout';
import useGroupedHolidays from 'scheduling/hooks/useGroupedHolidays';

const Wrapper = styled(FlexColumn)`
  ${card};
  flex: 1;
  overflow: hidden;
  opacity: ${({ isLoading }) => (isLoading ? 0.3 : 1)};
`;

const Container = styled(FlexColumn)`
  flex: 1;
  overflow: scroll;
  overscroll-behavior: none;
  overflow-scrolling: auto;
`;

const Content = styled(FlexColumn)`
  flex: 1;
  min-height: min-content;
  ${maxMedia.mobile`min-width:1200px;`};
`;

const LoaderWrapper = styled(FlexCenter)`
  z-index: 1;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 60px;
  right: 0;
`;

const Row = styled(Flex)`
  position: relative;
  ${({ isHeader }) =>
    isHeader ? `position: sticky; top: 0; z-index:4; background: ${({ theme }) => theme.colors.white};` : 'flex: 1;'};
`;

const HeaderCell = styled.div`
  padding: 10px 0;
  text-align: center;
`;

const Cell = styled(FlexColumn).attrs(() => ({ className: 'cell' }))`
  flex: 1 0 0%;
  min-width: 0px;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  padding: ${({ $dataCell }) => ($dataCell ? '0px' : '8px')};
  ${({ isHeader }) => !isHeader && 'min-height: 130px'};
  &:not(:first-child) {
    border-left: 1px solid ${({ theme }) => theme.colors.grey2};
  }

  cursor: ${({ onClick }) => onClick && 'pointer'};

  ${isTablet &&
  css`
    min-width: 200px;
  `}

  position: relative;
`;

const BarRow = styled.div`
  position: absolute;
  top: ${({ $topOffset }) => $topOffset || 51}px;
  left: 0;
  right: 0;
  z-index: 2;
`;

const WeekRowWrapper = styled(FlexColumn)`
  position: relative;
  flex: 1;
  width: 100%;
`;

const HOLIDAY_CHIP_HEIGHT = 20;
const PLANNER_BAR_HEIGHT = 28;
const MAIN_PROJECT_BAR_HEIGHT = 28;
const CHIPS_GAP = 4;

const MonthView = ({
  isLoading: isLoadingProjects,
  startDate: start,
  DayRenderer,
  userPermissions: { hasActionPermission }
}) => {
  const columns = useMemo(() => getDaysOfWeek(), []);
  const selectedBranchId = useRecoilValue(selectedBranch);

  const { mappedLabels, labels, isLoading: isLoadingLabels } = useLabels();
  const { mappedBranches, isLoading: isLoadingBranches } = useBranches();
  const { mappedLocations, isLoading: isLoadingLocations } = useLocations();
  const { mappedTags, isLoading: isLoadingTags } = useTags();
  const { mappedClientBranches, isLoading: isLoadingCRM } = useCRM();
  const { hasEngagedSuppliers, mappedSuppliers, mappedSupplierBranches } = useSuppliers();
  const { account, accountId, isLoading: isLoadingAccount } = useAccount();
  const projectFiltersForAccount = useRecoilValue(projectFilters)?.[account.id];
  const { accounts } = useUser();
  const { account: accountConfig } = useAccountConfig();
  const { push } = useHistory();
  const scrollerRef = useRef(null);

  const setSelectedEvent = useSetRecoilState(selectedEventModal);
  const setShowNewOrder = useSetRecoilState(newOrder);
  const setSelectedShift = useSetRecoilState(selectedShiftModal);
  const setSelectedBooking = useSetRecoilState(selectedBookingModal);
  const setPublishProject = useSetRecoilState(selectedPublishProject);
  const setCopyEvent = useSetRecoilState(selectedCopyEvent);
  const setAddQuickProject = useSetRecoilState(quickProject);
  const showAllBranches = useRecoilValue(showAllBranchesMode);
  const hideWeeklyPlanners = useRecoilValue(schedulingHideWeeklyPlanners);
  const showOnlyMainEvents = useRecoilValue(schedulingShowOnlyMainEvents);
  const setSelectedMainProjectView = useSetRecoilState(selectedMainProjectView);

  const { projectsByDate, weeklyPlannerProjects, mainProjectsByDate } = useContext(SchedulingContextSelector);

  const filteredMainProjectsByDate = useMemo(
    () => filterMainProjectsByDateForMainEventSelection(mainProjectsByDate, projectFiltersForAccount),
    [mainProjectsByDate, projectFiltersForAccount]
  );
  const { deleteProject, toggleArchiveProject } = useContext(SchedulingContextDispatcher);
  const { dateFormat } = useContext(ConfigContext);

  const isLoading =
    isLoadingProjects ||
    isLoadingLabels ||
    isLoadingBranches ||
    isLoadingLocations ||
    isLoadingTags ||
    isLoadingCRM ||
    isLoadingAccount;

  const data = useMemo(() => {
    const days = [];
    const startDate = moment(start).startOf('month');
    const endDate = moment(start).endOf('month');

    const daysBefore = startDate.diff(moment(startDate).startOf('week'), 'days');
    days.push(...[...Array(daysBefore)]);

    const daysInMonths = endDate.diff(startDate, 'days') + 1;

    days.push(
      ...[...Array(daysInMonths)].map(() => {
        const current = moment(startDate);
        startDate.add(1, 'day');
        return current;
      })
    );

    const daysAfter = moment(endDate).endOf('week').diff(endDate, 'days');
    days.push(...[...Array(daysAfter)]);

    const weeks = [];
    while (days.length) {
      weeks.push(days.splice(0, 7));
    }

    return weeks;
  }, [start]);

  const { timeFormat } = useContext(ConfigContext);

  const SORT_BY_START_TIME = useMemo(() => SORT_BY_TIME('startTime', timeFormat), [timeFormat]);

  const sortedProjects = useMemo(() => {
    const result = {};
    data.forEach((week) => {
      week.forEach((day) => {
        const formattedDate = day?.format?.(API_DATE_FORMAT);
        result[day] = showOnlyMainEvents
          ? []
          : projectsByDate[formattedDate]
              ?.filter((project) => !project.isWeeklyPlanner)
              ?.sort(({ dates: aDates }, { dates: bDates }) => {
                const minTimeA = aDates?.[formattedDate]?.minTime;
                const minTimeB = bDates?.[formattedDate]?.minTime;

                return SORT_BY_START_TIME(
                  { startTime: minTimeA && moment(minTimeA).format(timeFormat) },
                  { startTime: minTimeB && moment(minTimeB).format(timeFormat) }
                );
              });
      });
    });
    return result;
  }, [SORT_BY_START_TIME, data, projectsByDate, showOnlyMainEvents, timeFormat]);

  // Extract weekly planner projects and main projects for each week in a single pass
  const { weeklyPlannersByWeek, mainProjectsByWeek } = useMemo(() => {
    const planners = {};
    const mains = {};

    data.forEach((week, weekIndex) => {
      const weekPlanners = new Map();
      const weekMainProjects = new Map();
      const weekDates = new Set();

      week.filter(Boolean).forEach((day) => {
        const dateStr = day.format(API_DATE_FORMAT);
        weekDates.add(dateStr);

        // Collect main projects for this day
        const dayMainProjects = filteredMainProjectsByDate?.[dateStr] || [];
        dayMainProjects.forEach((project) => {
          if (!weekMainProjects.has(project.id)) {
            weekMainProjects.set(project.id, project);
          }
        });
      });

      // Check each weekly planner to see if it has dates in this week
      (weeklyPlannerProjects || []).forEach((project) => {
        const projectDates = Object.keys(project.dates || {});
        const hasDateInWeek = projectDates.some((date) => weekDates.has(date));
        if (hasDateInWeek && !weekPlanners.has(project.id)) {
          weekPlanners.set(project.id, project);
        }
      });

      planners[weekIndex] = Array.from(weekPlanners.values());
      mains[weekIndex] = Array.from(weekMainProjects.values());
    });

    return { weeklyPlannersByWeek: planners, mainProjectsByWeek: mains };
  }, [data, weeklyPlannerProjects, filteredMainProjectsByDate]);

  const mainProjectTrackCounts = useMemo(
    () => data.map((week, i) => getMainProjectBarTrackCount(mainProjectsByWeek[i], week)),
    [data, mainProjectsByWeek]
  );

  const onWeeklyPlannerClick = useCallback(
    ({ weekStartDate, weekEndDate }) => {
      const startDate = moment(weekStartDate).format(API_DATE_FORMAT);
      const endDate = moment(weekEndDate).format(API_DATE_FORMAT);

      const url = `/${account?.id}/scheduling?key=weekly&startDate=${startDate}&endDate=${endDate}&groupBy=employee&date=weekly&branchId=${selectedBranchId}&isArchived=0&listView=0`;
      window.open(url, '_blank');
    },
    [account?.id, selectedBranchId]
  );

  const firstDayOfWeek = accountConfig?.firstDayOfWeek ?? 0;

  const { holidaysByDate, maxHolidaysPerWeek } = useGroupedHolidays({ firstDayOfWeek });

  const allowQuickJob = useMemo(() => mappedBranches[selectedBranchId]?.scheduleConfig?.allowQuickJob, [
    mappedBranches,
    selectedBranchId
  ]);

  const preventCellClick = useMemo(() => allowQuickJob || showAllBranches, [allowQuickJob, showAllBranches]);

  const { isRestrictedFromEmployees } = accounts.find(({ id }) => id === accountId) || {};
  const alwaysAllowAddEventByAccountRoleId = isRestrictedFromEmployees;

  const onCellClick = useCallback(
    (day) => {
      if (!hasActionPermission && !alwaysAllowAddEventByAccountRoleId) {
        return;
      }
      const formattedDate = day?.format?.(API_DATE_FORMAT);

      if (account?.isHost) {
        return;
      }

      if (!preventCellClick) {
        trackEvent('Create Event - Event Creation Modal Opened', { referral: 'Calendar' });
        return setSelectedEvent({ date: formattedDate });
      }

      return null;
    },
    [account?.isHost, alwaysAllowAddEventByAccountRoleId, hasActionPermission, preventCellClick, setSelectedEvent]
  );

  return (
    <Wrapper isLoading={isLoading}>
      {isLoading && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}

      <Container ref={scrollerRef}>
        <Content>
          <Row isHeader>
            {columns.map(({ day }, index) => (
              <Cell key={index} isHeader>
                <HeaderCell>
                  <Trans>{day}</Trans>
                </HeaderCell>
              </Cell>
            ))}
          </Row>
          {data.map((week, index) => {
            // Stack: weekly planner strip → holiday chips (in cells) → main project strip
            // First/last weeks are filled with empty slots (prev/next month days), so take the first actual day of the week
            const firstDayInWeek = week.find(Boolean);
            const weekInYearIndex = firstDayInWeek ? getWeekKeyForDate(firstDayInWeek, firstDayOfWeek) : null;
            const maxHolidaysInWeek = weekInYearIndex ? maxHolidaysPerWeek?.[weekInYearIndex] || 0 : 0;
            // Base top: 51px (day header), plus 30px chip + 4px margin-bottom per holiday
            const plannerTopOffset = 51 + maxHolidaysInWeek * (HOLIDAY_CHIP_HEIGHT + CHIPS_GAP);

            const weeklyPlannerCount =
              hideWeeklyPlanners || showOnlyMainEvents ? 0 : weeklyPlannersByWeek[index]?.length || 0;
            // Each planner is 28px height + 4px gap, plus 4px top margin
            const plannerRowHeight =
              weeklyPlannerCount > 0 ? CHIPS_GAP + weeklyPlannerCount * (PLANNER_BAR_HEIGHT + CHIPS_GAP) : 0;

            const mainProjectTopOffset = plannerTopOffset + (plannerRowHeight ? plannerRowHeight - CHIPS_GAP : 0);
            const mainProjectCount = mainProjectTrackCounts[index];
            const mainProjectRowHeight =
              mainProjectCount > 0 ? mainProjectCount * (MAIN_PROJECT_BAR_HEIGHT + CHIPS_GAP) : 0;

            return (
              <WeekRowWrapper key={index}>
                {!hideWeeklyPlanners && !showOnlyMainEvents && weeklyPlannersByWeek[index]?.length > 0 && (
                  <BarRow $topOffset={plannerTopOffset}>
                    <WeeklyPlannerBar
                      weeklyPlannerProjects={weeklyPlannersByWeek[index]}
                      onClick={onWeeklyPlannerClick}
                      weekDays={week}
                    />
                  </BarRow>
                )}
                {mainProjectCount > 0 && (
                  <BarRow $topOffset={mainProjectTopOffset}>
                    <MainProjectBar
                      mainProjects={mainProjectsByWeek[index]}
                      weekDays={week}
                      onClick={setSelectedMainProjectView}
                    />
                  </BarRow>
                )}
                <Row id={`month-view-row-${index}`}>
                  {week.map((day, columnIndex) => {
                    const isToday = day && moment().isSame(day, 'date');
                    const allowOnClick = !!day && !preventCellClick;
                    const dayWeekInYearIndex = day ? getWeekKeyForDate(day, firstDayOfWeek) : null;
                    return (
                      <Cell
                        key={columnIndex}
                        isToday={isToday}
                        onClick={allowOnClick ? () => onCellClick(day) : undefined}
                        $dataCell
                        data-testid="day-cell"
                        data-date-year={day?.year?.()}
                        data-date-month={(day?.month?.() ?? 0) + 1}
                        data-date-day={day?.date?.()}>
                        {isLoading ? null : (
                          <DayRenderer
                            weeklyPlannerRowHeight={plannerRowHeight}
                            mainProjectRowHeight={mainProjectRowHeight}
                            account={account}
                            currentSpecialDays={holidaysByDate?.[moment(day).format('YYYY-MM-DD')]}
                            maxHolidaysInCurrentWeek={maxHolidaysPerWeek?.[dayWeekInYearIndex]}
                            push={push}
                            selectedBranchId={selectedBranchId}
                            hasEngagedSuppliers={hasEngagedSuppliers}
                            setShowNewOrder={setShowNewOrder}
                            setSelectedEvent={setSelectedEvent}
                            setSelectedBooking={setSelectedBooking}
                            setSelectedShift={setSelectedShift}
                            setPublishProject={setPublishProject}
                            setCopyEvent={setCopyEvent}
                            setAddQuickProject={setAddQuickProject}
                            mappedLabels={mappedLabels}
                            labels={labels}
                            mappedBranches={mappedBranches}
                            mappedLocations={mappedLocations}
                            mappedTags={mappedTags}
                            mappedClientBranches={mappedClientBranches}
                            mappedSuppliers={mappedSuppliers}
                            mappedSupplierBranches={mappedSupplierBranches}
                            date={day}
                            formattedDate={day?.format?.(API_DATE_FORMAT)}
                            columnIndex={columnIndex}
                            dateFormat={dateFormat}
                            sortedProjects={sortedProjects[day]}
                            deleteProject={deleteProject}
                            toggleArchiveProject={toggleArchiveProject}
                            isToday={isToday}
                            weekIndex={index}
                            scrollerRef={scrollerRef}
                          />
                        )}
                      </Cell>
                    );
                  })}
                </Row>
              </WeekRowWrapper>
            );
          })}
        </Content>
      </Container>
    </Wrapper>
  );
};

export default withPermissions(MonthView, {
  action: { privileges: [privileges.EDIT_SCHEDULING] }
});
